// Edit this file and refresh the page. Every card needs a unique id.
// links: [fromId, toId]. Problems link to solutions, solutions link to effects.
// Solution layer is one of: interface | harness | training | tooling
// severity (problems and effects): 1..3, impact on agent work, shown as three dots
// Source tags: D0831 = daily 2026-08-31, CATCH = catch-up, DEEP = harness deep dive, D0907 = daily 2026-09-07

const MAP = {
  problems: [
    { group: "Compaction and context", cards: [
      { id: "p_compaction", severity: 3, title: "Compaction destroys constraints",
        detail: "53% of safety rules survive one round, 10% survive five. The nine-section summary loses early instructions; only things outside message history are guaranteed to survive.",
        src: ["D0831", "CATCH", "DEEP"] },
      { id: "p_anxiety", severity: 2, title: "Context anxiety",
        detail: "Compaction leaves the model wrapping up early as the window fills, even when there is room.",
        src: ["DEEP"] },
      { id: "p_failure_modes", severity: 3, title: "Context failure modes",
        detail: "Poisoning, distraction, confusion, clash: too much or wrong context degrades output before any limit is hit.",
        src: ["CATCH"] },
    ]},
    { group: "Write policy", cards: [
      { id: "p_no_feedback", severity: 3, title: "No feedback on what to keep",
        detail: "Write policy does not learn from outcomes. Nobody ships click-feedback for memory; the weak stage of Store, Retrieve, Feedback is feedback.",
        src: ["CATCH", "DEEP"] },
      { id: "p_bias", severity: 2, title: "Structural bias in what survives",
        detail: "Each design over-remembers one thing: what the user said aloud, what the model cited, what got searched, what a regex recognises.",
        src: ["DEEP"] },
    ]},
    { group: "Forgetting and truth", cards: [
      { id: "p_stale", severity: 3, title: "Stale facts, no cascade",
        detail: "Facts change and nothing invalidates the conclusions built on them. Contradictions accumulate.",
        src: ["D0831", "CATCH"] },
      { id: "p_context_stripped", severity: 2, title: "Facts stripped of context",
        detail: "High-modernist graphs and pgvector+Neo4j+LLM stacks reduce facts until they are senseless.",
        src: ["D0831"] },
    ]},
    { group: "Retrieval", cards: [
      { id: "p_retrieval_miss", severity: 3, title: "Retrieval that is not semantic",
        detail: "Index-by-description side-calls, grep, or per-repo local stores; the exact error string never made it into the notes.",
        src: ["DEEP", "D0907"] },
      { id: "p_model_must_call", severity: 2, title: "Recall depends on the model asking",
        detail: "The layer works only if the model reliably calls memory_search; hooks do not fire under claude-cli.",
        src: ["DEEP"] },
    ]},
    { group: "Budgets, horizon, trust", cards: [
      { id: "p_budget", severity: 2, title: "Hard caps and silent horizon",
        detail: "200 lines / 25KB, 60k chars, 30 unused days, rules fade in 15 turns. Everyone answers 'until the index overflows'.",
        src: ["DEEP"] },
      { id: "p_trust", severity: 2, title: "No trust or control surface",
        detail: "Policy (when to write, keep, consolidate, compact) is not user-replaceable; some memories could not be deleted.",
        src: ["DEEP"] },
      { id: "p_cold_start", severity: 2, title: "Cold start every session",
        detail: "25 years of emails, meetings and decisions that every session started without.",
        src: ["DEEP"] },
    ]},
    { group: "Procedural memory", cards: [
      { id: "p_procedural", severity: 2, title: "Procedure does not transfer",
        detail: "The challenge is extracting procedural structure that stays useful beyond the environment where it was learned.",
        src: ["DEEP"] },
    ]},
    { group: "Evaluation", cards: [
      { id: "p_eval", severity: 1, title: "Numbers that do not carry meaning",
        detail: "Recall rate misses imprecise binding; long-context is a cost baseline you must beat; benchmark-per-month sprawl.",
        src: ["CATCH", "D0907"] },
    ]},
  ],

  solutions: [
    { group: "Compaction alternatives", cards: [
      { id: "s_notes_reset", layer: "harness", title: "Running notes + clean window + search back",
        detail: "Astra: the model writes notes as it goes, new_context starts a fresh window without summarizing, earlier windows stay searchable.",
        src: ["D0907"] },
      { id: "s_handoff", layer: "harness", title: "Reset with structured handoff",
        detail: "Full resets with a handoff artifact between planner and worker; Codex ships checkpoint-and-restart instead of summarization.",
        src: ["DEEP"] },
      { id: "s_trained_compaction", layer: "training", title: "Trained self-summarization",
        detail: "Cursor Composer: synthetic summarize query at fixed token counts, ~1,000-token summaries, 50% less compaction error at one fifth the tokens.",
        src: ["DEEP"] },
      { id: "s_steerable_compaction", layer: "interface", title: "Steerable compaction prompt",
        detail: "Compact Instructions in CLAUDE.md, /compact focus, prompt asked to preserve sensitive user instructions verbatim.",
        src: ["DEEP"] },
      { id: "s_outside_history", layer: "harness", title: "Keep durable state outside history",
        detail: "Memory files, a silent flush turn before compaction, long tool output written to files and read with tail.",
        src: ["DEEP"] },
    ]},
    { group: "Reframing: reading, not remembering", cards: [
      { id: "s_stream_scratchpad", layer: "training", title: "Streaming scratchpad instead of a memory store",
        detail: "MemAgent: treat long input as something to read with a small window, not something to store. A fixed 1024-token memory is overwritten after every 5000-token chunk; the model only ever sees question + memory + chunk. Trained with RL where the single reward is exact match on the final answer, copied to every step. 14B model: 71% at 3.5M tokens where the 1M-context baseline scores 0.",
        src: ["CATCH"] },
      { id: "s_ttt", layer: "training", title: "Context folded into weights while reading (TTT-E2E)",
        detail: "Session memory, not long-term. The model takes a gradient step on every 1K-token chunk it reads, so the conversation ends up in its MLP weights instead of a KV cache; only the last 8K tokens stay verbatim. For a session like this one: no compaction cliff, flat cost per turn, no context anxiety, a gist of everything said. Only a model lab can build it (pretraining objective + new serving). Pair it with a searchable exact store; the weights keep gist, not strings.",
        src: ["CATCH"] },
    ]},
    { group: "Write policy and feedback", cards: [
      { id: "s_usage_signal", layer: "harness", title: "Usage-based survival",
        detail: "Codex: citations drive usage_count, which drives ranking and 30-day expiry. OpenClaw: recall counts feed dreaming.",
        src: ["DEEP"] },
      { id: "s_gated_write", layer: "harness", title: "Prompt-gated writes",
        detail: "Save only what the user corrected or confirmed, not the model's own findings.",
        src: ["DEEP"] },
      { id: "s_rl_memory", layer: "training", title: "RL over the write policy",
        detail: "Instead of a hand-written rule for what to save, train the model with RL so it learns which memory writes helped. Memory-R1: a manager picks ADD / UPDATE / DELETE / NOOP per turn, rewarded only by whether a frozen answer agent then gets the question right (152 training questions). MemAgent: a 1024-token memory overwritten chunk by chunk, rewarded by exact match on the final answer; 8K window, 71% at 3.5M tokens.",
        src: ["CATCH"] },
    ]},
    { group: "Consolidation and forgetting", cards: [
      { id: "s_dreaming", layer: "harness", title: "Consolidation sweeps (dreaming)",
        detail: "Light / REM / Deep phases; six weighted signals (relevance .30, frequency .24, ...); nightly enrich, dedup, contradiction probe.",
        src: ["DEEP"] },
      { id: "s_dependency", layer: "tooling", title: "Deterministic dependency tracking",
        detail: "Datalog store: LLM only types facts at the edges; retract A implies B and every dependent conclusion is invalidated. supersedes / depends_on fields next.",
        src: ["D0831", "CATCH"] },
      { id: "s_provenance", layer: "interface", title: "Provenance and soft forgetting",
        detail: "Takes vs facts (holder + confidence + timestamp); forgetting is a strikethrough plus reason, never a delete; provenance tags and /trace.",
        src: ["DEEP"] },
    ]},
    { group: "Substrate", cards: [
      { id: "s_wiki", layer: "tooling", title: "Files as the store, LLM-maintained wiki",
        detail: "Karpathy loop: immutable sources, compiled entity and concept pages, contradictions note, lint pass. The store is a folder.",
        src: ["CATCH"] },
      { id: "s_git_store", layer: "tooling", title: "Memory store as a git repo",
        detail: "Diff-driven forgetting; the agent's memory is its task graph, versioned.",
        src: ["CATCH", "DEEP"] },
      { id: "s_split", layer: "harness", title: "Split knowledge from behaviour",
        detail: "Compiled wiki for world knowledge, budgeted profile for how the agent operates; GBrain holds facts about external entities, the harness holds operation.",
        src: ["CATCH", "DEEP"] },
    ]},
    { group: "Retrieval", cards: [
      { id: "s_hybrid", layer: "tooling", title: "Hybrid retrieval pipeline",
        detail: "BM25 + embeddings + entity linking, 30-day half-life, MMR, RRF, graph augmentation, cross-encoder rerank. The commodity architecture.",
        src: ["CATCH", "DEEP"] },
      { id: "s_trained_embed", layer: "training", title: "Embeddings trained from agent traces",
        detail: "Cursor's trick: take logs of real agent sessions, have an LLM judge which file should have been retrieved at each step, then train the embedding model to rank those files higher. +12.5% on codebase Q&A. Retrieval learning from usage, the feedback loop missing everywhere else.",
        src: ["DEEP"] },
      { id: "s_intent_gate", layer: "harness", title: "Intent-gated recall",
        detail: "Escalate only on recall intent (temporal, multi-hop); deterministic intent classification (entity / temporal / event / concept).",
        src: ["DEEP"] },
      { id: "s_subagent", layer: "harness", title: "Retrieval sub-agent",
        detail: "Active Memory side-call that searches so the main context does not carry the search.",
        src: ["DEEP"] },
    ]},
    { group: "Into the model", cards: [
      { id: "s_model_primitive", layer: "training", title: "Memory as a model-side primitive",
        detail: "Memory stops being a plugin and gets trained into the model: Anthropic says memory quality is now trained against internal benchmarks, Codex's code assumes trained compaction behaviour, Cursor trains Composer inside the same harness it ships. The write and compaction rules you can read in prompts today are a snapshot of behaviour on its way into the weights.",
        src: ["CATCH", "DEEP"] },
    ]},
    { group: "Procedural memory", cards: [
      { id: "s_skills", layer: "tooling", title: "Skills and behaviour caches",
        detail: "Remember how to do things, not facts. Muscle-Mem stores successful action sequences and replays them. 'Thin harness, fat skills' keeps the agent core small and puts the know-how in ~75 skill files; skillopt tunes a skill file the way you would tune a model parameter.",
        src: ["CATCH", "DEEP"] },
    ]},
    { group: "Control surface", cards: [
      { id: "s_pins_budgets", layer: "interface", title: "Pins and explicit budgets",
        detail: "Up to four pinned memories; 60k-char bootstrap budget; visible caps instead of silent truncation.",
        src: ["DEEP"] },
      { id: "s_manager", layer: "interface", title: "Memory manager for the person",
        detail: "A UI where you can see and control what the agent remembers: provenance tags, a trace of what was recalled, a forget command, a delete button. Today OpenClaw has most of this, Claude Code prints 'Recalled 2 memories', Codex has nothing, Cursor's memories could not be deleted.",
        src: ["DEEP"] },
    ]},
    { group: "Evaluation", cards: [
      { id: "s_benchmarks", layer: "tooling", title: "Benchmarks that probe the failure",
        detail: "Newer tests built to catch what older ones missed: BEAM (10M-token conversations, can the system resolve contradictions and order events), LongMemEval-V2 (agents with 115M-token histories), HaluMem (does the memory operation itself hallucinate), MemoryArena (tasks that depend on earlier sessions).",
        src: ["CATCH"] },
    ]},
  ],

  effects: [
    { group: "Cost", cards: [
      { id: "e_cost", severity: 2, title: "The trade is now cost",
        detail: "Resets, re-reads and side-calls burn tokens; long-context is a real baseline on cost, not just accuracy.",
        src: ["CATCH", "DEEP"] },
    ]},
    { group: "Opacity", cards: [
      { id: "e_unreadable", severity: 2, title: "Unreadable, uncitable artifacts",
        detail: "Encrypted compaction preserves latent understanding but cannot be read, steered or cited.",
        src: ["DEEP"] },
      { id: "e_explicit_miss", severity: 1, title: "Silent deletion becomes explicit miss",
        detail: "Better, but the notes are an index and the index can be incomplete; you still have to know to search.",
        src: ["D0907"] },
    ]},
    { group: "Scope", cards: [
      { id: "e_exact_recall", severity: 3, title: "Gist survives, exact strings do not",
        detail: "TTT-E2E scores 0.03 to 0.06 on needle-in-a-haystack against 0.64 to 1.00 for full attention. Card ids, ports, URLs, the exact old_string an edit needs: all fuzzy. The model must re-read files far more often.",
        src: ["CATCH"] },
      { id: "e_session_only", severity: 2, title: "Ends with the session",
        detail: "The weight updates live for one sequence. Nothing in the paper carries them to the next session, so CLAUDE.md, MEMORY.md and the repo stay the only long-term layer.",
        src: ["CATCH"] },
      { id: "e_constraint_unknown", severity: 2, title: "Constraint survival unmeasured",
        detail: "Summarization keeps narrative and drops rules (53%, then 10%). Whether next-token gradient steps keep a one-line rule like 'never use em dashes' any better is not measured; the paper reports perplexity, not instruction adherence. Could be worse.",
        src: ["CATCH", "D0831"] },
    ]},
    { group: "Ownership and lock-in", cards: [
      { id: "e_vendor", severity: 2, title: "Absorbed by the model vendor",
        detail: "Memory features are the most likely to move into the model; extraction defaults to specific vendor models, unavailable on API keys or Bedrock.",
        src: ["DEEP"] },
      { id: "e_snapshot", severity: 1, title: "Today's prompts are a snapshot",
        detail: "80% of the system prompt removable per new model; harness behaviour migrates into training and design ownership moves with it.",
        src: ["DEEP"] },
      { id: "e_privacy", severity: 2, title: "Data leaves the machine",
        detail: "Chunks embedded server-side, summaries on vendor servers; obfuscated paths help but the vectors are not local.",
        src: ["DEEP"] },
    ]},
    { group: "Bias", cards: [
      { id: "e_survivorship", severity: 2, title: "Survivorship bias",
        detail: "What the model cites survives, what it silently used does not; usage frequency is not significance; one bug zeroed the counter.",
        src: ["DEEP"] },
      { id: "e_maintenance_not_usage", severity: 2, title: "Maintenance loop, not usage loop",
        detail: "Deleting contradicted facts is housekeeping; it never tells the ranker what mattered.",
        src: ["DEEP"] },
      { id: "e_vocab", severity: 1, title: "Someone else's vocabulary",
        detail: "Regex entity linking over a VC's term list decides what gets recognised at all.",
        src: ["DEEP"] },
    ]},
    { group: "Reliance", cards: [
      { id: "e_model_reliance", severity: 3, title: "Only as good as the tool call",
        detail: "If the model does not call search, nothing is recalled; hooks that do not fire take the layer with them.",
        src: ["DEEP"] },
      { id: "e_db_truth", severity: 2, title: "'Markdown is truth' drifts to 'DB is truth'",
        detail: "Once the index is what gets queried, the files stop being the source of record.",
        src: ["DEEP"] },
    ]},
    { group: "Consolidation harms", cards: [
      { id: "e_false_binding", severity: 3, title: "False connections",
        detail: "Replay-based consolidation fails by imprecise binding across related items, not by loss; recall rate would not detect it.",
        src: ["D0907"] },
      { id: "e_horizon", severity: 2, title: "Horizon still unsolved",
        detail: "Explicit caps (200 lines, 25KB, 60k chars, 30 unused days) make truncation visible, but nobody has answered how long a memory should live. Everyone's real answer is still 'until the index fills up'.",
        src: ["DEEP"] },
    ]},
    { group: "Evidence gap", cards: [
      { id: "e_skills_override", severity: 3, title: "Skills override better native behaviour",
        detail: "Self-generated skills give no benefit on average; models cannot author the procedural knowledge they benefit from; shipping ahead of evidence.",
        src: ["DEEP"] },
      { id: "e_lab_only", severity: 1, title: "Lab-only with counter-evidence",
        detail: "RL-over-memory has no shipped product and mixed results.",
        src: ["CATCH"] },
      { id: "e_gold_answer", severity: 2, title: "Needs a gold answer to reward against",
        detail: "Memory-R1 and MemAgent both reward only answer correctness on QA benchmarks where a right answer exists. Real agent memory rarely has one, so the learned policy is tuned to the benchmark's question types, not to the work.",
        src: ["CATCH"] },
      { id: "e_benchmark_sprawl", severity: 1, title: "Benchmark sprawl",
        detail: "A new memory benchmark almost every month, and scores swing hard with scale (Mem0 drops from 64.1 to 48.6 going from 1M to 10M tokens). The numbers move faster than the methods, so a single score tells you little.",
        src: ["CATCH"] },
    ]},
    { group: "Burden", cards: [
      { id: "e_curation", severity: 2, title: "Curation burden, single operator",
        detail: "The file-and-wiki approach works because a person keeps it tidy: running the lint pass, fixing stale claims, deciding what belongs. That does not scale past one owner; GBrain is explicitly single-operator.",
        src: ["CATCH", "DEEP"] },
    ]},
  ],

  links: [
    // problems -> solutions
    ["p_compaction", "s_notes_reset"], ["p_compaction", "s_handoff"], ["p_compaction", "s_trained_compaction"],
    ["p_compaction", "s_steerable_compaction"], ["p_compaction", "s_outside_history"],
    ["p_anxiety", "s_handoff"], ["p_anxiety", "s_notes_reset"],
    ["p_failure_modes", "s_notes_reset"], ["p_failure_modes", "s_intent_gate"], ["p_failure_modes", "s_pins_budgets"],
    ["p_no_feedback", "s_usage_signal"], ["p_no_feedback", "s_rl_memory"], ["p_no_feedback", "s_gated_write"],
    ["p_no_feedback", "s_dreaming"], ["p_no_feedback", "s_trained_embed"],
    ["p_bias", "s_provenance"], ["p_bias", "s_gated_write"], ["p_bias", "s_usage_signal"],
    ["p_stale", "s_dependency"], ["p_stale", "s_dreaming"], ["p_stale", "s_provenance"], ["p_stale", "s_wiki"],
    ["p_context_stripped", "s_wiki"], ["p_context_stripped", "s_provenance"],
    ["p_retrieval_miss", "s_hybrid"], ["p_retrieval_miss", "s_trained_embed"], ["p_retrieval_miss", "s_notes_reset"],
    ["p_model_must_call", "s_intent_gate"], ["p_model_must_call", "s_subagent"],
    ["p_budget", "s_dreaming"], ["p_budget", "s_git_store"], ["p_budget", "s_pins_budgets"], ["p_budget", "s_split"],
    ["p_trust", "s_steerable_compaction"], ["p_trust", "s_provenance"], ["p_trust", "s_manager"],
    ["p_cold_start", "s_wiki"], ["p_cold_start", "s_split"],
    ["p_procedural", "s_skills"], ["p_procedural", "s_rl_memory"],
    ["p_eval", "s_benchmarks"],
    ["p_compaction", "s_stream_scratchpad"], ["p_failure_modes", "s_stream_scratchpad"], ["p_budget", "s_stream_scratchpad"],
    ["p_compaction", "s_ttt"], ["p_anxiety", "s_ttt"], ["p_budget", "s_ttt"],

    // solutions -> effects
    ["s_notes_reset", "e_explicit_miss"], ["s_notes_reset", "e_cost"],
    ["s_handoff", "e_cost"], ["s_handoff", "e_explicit_miss"],
    ["s_trained_compaction", "e_vendor"], ["s_trained_compaction", "e_unreadable"], ["s_trained_compaction", "e_snapshot"],
    ["s_steerable_compaction", "e_snapshot"],
    ["s_outside_history", "e_horizon"],
    ["s_usage_signal", "e_survivorship"], ["s_usage_signal", "e_maintenance_not_usage"],
    ["s_gated_write", "e_survivorship"],
    ["s_rl_memory", "e_lab_only"], ["s_rl_memory", "e_gold_answer"],
    ["s_stream_scratchpad", "e_lab_only"], ["s_stream_scratchpad", "e_gold_answer"], ["s_stream_scratchpad", "e_explicit_miss"],
    ["s_ttt", "e_exact_recall"], ["s_ttt", "e_session_only"], ["s_ttt", "e_constraint_unknown"], ["s_ttt", "e_vendor"], ["s_ttt", "e_unreadable"], ["s_ttt", "e_lab_only"],
    ["s_dreaming", "e_false_binding"], ["s_dreaming", "e_maintenance_not_usage"],
    ["s_dependency", "e_curation"],
    ["s_provenance", "e_curation"],
    ["s_wiki", "e_db_truth"], ["s_wiki", "e_curation"],
    ["s_git_store", "e_survivorship"],
    ["s_split", "e_curation"],
    ["s_hybrid", "e_vocab"], ["s_hybrid", "e_cost"],
    ["s_trained_embed", "e_vendor"], ["s_trained_embed", "e_privacy"],
    ["s_intent_gate", "e_model_reliance"],
    ["s_subagent", "e_model_reliance"], ["s_subagent", "e_cost"],
    ["s_model_primitive", "e_unreadable"], ["s_model_primitive", "e_vendor"], ["s_model_primitive", "e_snapshot"],
    ["s_skills", "e_skills_override"],
    ["s_pins_budgets", "e_horizon"],
    ["s_manager", "e_privacy"],
    ["s_benchmarks", "e_benchmark_sprawl"],
  ],
};

export default MAP;
